import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  EsteiraTagValorItem,
  getCarTagValores,
  upsertCarTagValores,
} from '../../../api/esteiraTagsApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { notify } from '../../../shared/feedback/notify';

interface MotivoEsteiraModalProps {
  isOpen: boolean;
  carId: string | null;
  /** Esteira atual do veículo — aceita id ou chave. */
  esteiraAtual?: string | null;
  onCancel: () => void;
  onSaved?: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Edição do motivo / tags da ESTEIRA ATUAL do veículo (at-89, 03/09/2026).
 *
 * Antes, tag de esteira só era preenchida no cadastro e na edição do veículo — o motivo pelo qual
 * o veículo estava parado "passava muito batido" (Victor). Este modal dá a ela uma ação própria,
 * a um clique do modal do veículo.
 *
 * 🔴 Só as tags da esteira ATUAL. O backend devolve todas as definições ativas do papel, e cada
 * esteira tem a sua "Motivo da pendência" — sem o filtro, seriam seis campos de mesmo nome.
 *
 * ⚠️ Envia SÓ as tags exibidas. O `upsertCarTagValores` é um upsert por `definicaoId`, então
 * mandar apenas estas preserva o que estiver preenchido nas outras esteiras — mandar o mapa
 * inteiro apagaria o histórico das etapas anteriores.
 */
const MotivoEsteiraModal: React.FC<MotivoEsteiraModalProps> = ({
  isOpen,
  carId,
  esteiraAtual,
  onCancel,
  onSaved,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { idDe, labelDe, isFetched: esteirasCarregadas, isError: esteirasFalharam } = useEsteiras();
  const [tags, setTags] = useState<EsteiraTagValorItem[]>([]);
  const [valores, setValores] = useState<Record<string, string | null>>({});
  const [carregando, setCarregando] = useState(false);
  const [cargaFalhou, setCargaFalhou] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // at-134: o recorte pela esteira atual é da ROTA (`esteiraId`), não mais um filtro local.
  const esteiraId = idDe(esteiraAtual);

  const carregar = useCallback(async () => {
    if (!carId) return;
    if (!esteiraId) {
      // Sem a lista de esteiras não há id a pedir. Falha dela é falha (bloqueia o salvar),
      // nunca "esta esteira não tem motivo".
      if (esteirasFalharam) {
        setTags([]);
        setCargaFalhou(true);
      } else if (esteirasCarregadas || !esteiraAtual) {
        setTags([]);
        setCargaFalhou(false);
      }
      return;
    }
    setCarregando(true);
    try {
      const daEsteira = await getCarTagValores(carId, esteiraId);
      setTags(daEsteira);
      setValores(
        daEsteira.reduce<Record<string, string | null>>((acc, t) => {
          acc[t.definicaoId] = t.valor;
          return acc;
        }, {}),
      );
      setCargaFalhou(false);
    } catch (error) {
      setTags([]);
      setCargaFalhou(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar os motivos desta esteira. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [carId, esteiraId, esteiraAtual, esteirasCarregadas, esteirasFalharam]);

  useEffect(() => {
    if (isOpen) void carregar();
  }, [isOpen, carregar]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const aoClicarFora = (event: MouseEvent) => {
      const alvo = event.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onCancel();
    };

    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [isOpen, onCancel, sidebarRef]);

  if (!isOpen || !carId) return null;

  const salvar = async () => {
    setSalvando(true);
    try {
      await upsertCarTagValores(
        carId,
        tags.map((t) => ({ definicaoId: t.definicaoId, valor: valores[t.definicaoId] ?? null })),
      );
      notify.success('Motivo atualizado.');
      onSaved?.();
      onCancel();
    } catch (error) {
      // Sem toast de sucesso quando falha: o motivo continua o que era (at-60).
      notify.errorFrom(error, 'Não foi possível salvar o motivo desta esteira.');
    } finally {
      setSalvando(false);
    }
  };

  const nomeEsteira = labelDe(esteiraAtual) || 'esteira atual';

  return (
    // `.status-modal-overlay` já fica um nível acima do modal de ações (shared/ui/modal-overlay.css).
    <div className="status-modal-overlay">
      <div className="status-modal-content" ref={modalRef}>
        <div className="LightboxStatus">
          <div className="LightboxStatusForm">
            <div className="EditarCarro">Motivo da pendência — {nomeEsteira}</div>

            {carregando && <p style={{ fontSize: 13, color: 'var(--tema-texto-2, #5E5E5E)' }}>Carregando...</p>}

            {!carregando && cargaFalhou && (
              <p style={{ fontSize: 13, color: 'var(--tema-texto-vermelho, #b91c1c)' }}>
                Não foi possível carregar os motivos. Salvar agora poderia sobrescrever o que já
                estava preenchido — recarregue a página antes de alterar.
              </p>
            )}

            {!carregando && !cargaFalhou && tags.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--tema-texto-2, #5E5E5E)' }}>
                A etapa <strong>{nomeEsteira}</strong> não tem motivo cadastrado. Quem configura é o
                administrador do sistema, em Tags de Esteira.
              </p>
            )}

            {!carregando &&
              !cargaFalhou &&
              tags.map((tag) => (
                <div className="CampoStatus" key={tag.definicaoId}>
                  <div className="CampoTitulo">{tag.nome}</div>
                  <div className="DropDownFormulRioStatus">
                    <div className="InputSelect">
                      {tag.opcoes.length > 0 ? (
                        <select
                          className="Input"
                          value={valores[tag.definicaoId] ?? ''}
                          onChange={(e) =>
                            setValores((prev) => ({
                              ...prev,
                              [tag.definicaoId]: e.target.value || null,
                            }))
                          }
                        >
                          <option value="">Selecione...</option>
                          {tag.opcoes.map((op) => (
                            <option key={op.valor} value={op.valor}>
                              {op.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="Input"
                          type="text"
                          value={valores[tag.definicaoId] ?? ''}
                          onChange={(e) =>
                            setValores((prev) => ({
                              ...prev,
                              [tag.definicaoId]: e.target.value || null,
                            }))
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}

            <div className="Frame1000003998Status">
              <div className="CtaBigEditar cancel">
                <button type="button" className="CustomFileCancel" onClick={onCancel}>
                  Cancelar
                </button>
              </div>
              <button
                type="button"
                className="CtaBigEditar save"
                onClick={() => void salvar()}
                disabled={salvando || carregando || cargaFalhou || tags.length === 0}
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotivoEsteiraModal;
